import { useLoaderData, useLocation } from '@remix-run/react';
import { ClientOnly } from 'remix-utils/client-only';
import type { Feed, ScreenType, UIComponent } from '~/services/layout/index.ts';
/* COMPONENTS */
import GeneralContent from '~/plugins/general-content/components/index.tsx';
import NavBar from '~/components/navigation/nav-bar.tsx';
import NewNavBar, {
  getCurrentNavBar,
} from '~/services/plugins/nav-bar/components';
import NetworkStatusBanner from '~/components/network-status-banner.tsx';
import Footer from '~/components/navigation/footer.tsx';
import HookLoading from '~/components/hook-loading.tsx';
/* TYPES */
import type { HeadersFunction, MetaFunction } from '@remix-run/node';
import { useScreenAnalytics } from '~/hooks/use-screen-analytics.ts';
import {
  cssVarsMetaFunctionData,
  getAdditionalSearchScreenId,
} from '~/services/css-vars/index.ts';
import { useScreenHooks } from '~/hooks/use-screen-hooks.ts';

import type { LoaderFunctionArgs } from '@remix-run/node';
import { json, redirect } from '@remix-run/node';
import {
  addEndpointsTagsToUiComponents,
  getLinksToScreenData,
  getScreenIdByScreenType,
} from '~/services/layout/index.server.ts';
import { getSearchScreens } from '~/services/layout/index.ts';
import { Cause, Effect, Exit, Option } from 'effect';
import {
  getDebugMode,
  getLayoutIdOverride,
} from '~/services/debug/index.server.ts';
import { maybeLoginWithToken } from '~/services/login/index.ts';
import type { GetSearchScreenData } from '~/services/router/index.server';
import {
  getIsSearchScreen,
  getSearchScreenData,
  getSearchFilterEntries,
  getStaticLinks,
  getWebviewUrl,
  addWebLinks,
  getCurrentScreen2,
  maybeRedirectToPlayer,
} from '~/services/router/index.server';
import { getIsTabsScreen } from '~/utils/screen-utils';
import { getHashForCSS } from '~/utils/hash.ts';
import type { NavigationHandler } from '~/services/layout/navigations.ts';
import {
  getNavItems,
  getScreenNavigations,
} from '~/services/layout/navigations.ts';
import type { FooterData } from '~/components/navigation/footer.tsx';
import type { ServerLoadedFeeds } from '~/services/layout/server-loaded-feeds.ts';
import { type Screen } from '~/services/layout/index.ts';
import {
  getServerLoadedFeeds,
  getScreenFeed,
  getTabsContentFeeds,
} from '~/services/layout/server-loaded-feeds.ts';
import { getBaseURL } from '~/utils/get-base-url.server.ts';
import getMetadata from '~/services/seo/get-metadata/index.ts';
import { getConfig, getConfigsCaching } from '~/services/config/index.ts';
import { ZappConfig } from '~/services/zapp-config/index.server.ts';
import { getAnalyticsSharedVars } from '~/services/analytics/shared-vars.server.ts';
import { isValidUrl } from '~/utils/validations.ts';
import type { ZappNavigation } from '~/services/layout/types';
import {
  getThemeVars,
  isThemeV2Enabled,
} from '~/services/css-vars/get-theme-vars';
import { RequestURL, ScreenReuqest } from '~/services/request-url';
import { getPluginConfig } from '~/utils/get-plugin-configuration';
import webPocManifest from '~/services/plugins/web-poc/config/manifest';
import cachingManifest from '~/services/plugins/caching/config/manifest';
import { screenHasSmartComponents } from '~/services/plugins/curation/components';
import userwayManifest from '~/services/plugins/userWay/config/manifest';
import clarityManifest from '~/services/plugins/microsoft-clarity/config/manifest';
import oneTrustManifest from '~/services/plugins/applicaster-cmp-onetrust/config/manifest';
import {
  getKetchScreen,
  getKetchScript,
} from '~/services/plugins/ketch-cmp/utils';
import favoritesManifest from '~/services/plugins/local_storage_favourites_action/config/manifest';
import continueWatchingManifest from '~/services/plugins/continue-watching/config/manifest';
import defaultThemeV2 from '~/services/plugins/default_theme_v2/config/manifest';
import navigationActionManifest from '~/services/plugins/navigation_action/config/manifest.ts';
import trailerActionManifest from '~/services/plugins/trailer_action/config/manifest.ts';
import {
  getDeviceType,
  getTimeZoneHeader,
} from '~/services/request/index.server';
import { useScreensHistory } from '~/hooks/use-screens-history';
import { useLoadIsLoggedIn, useMockIsLoggedIn } from '~/hooks/use-is-logged-in';
import { useLoadFeed } from '~/hooks/use-feed';
import { transformSmartComponents } from '~/services/plugins/curation/components';
import { useLanguageRedirect } from '~/hooks/use-language-redirect.ts';
import {
  getLanguagesLabels,
  getSupportedLanguages,
  getURLLanguage,
} from '~/utils/i18n';
import {
  hasClearCacheOnReloadRule,
  isEmptyArray,
  isTrue,
} from '~/services/layout/layout';
import {
  CACHE_CONTROL_NO_STORE,
  getPageCacheControlHeader,
} from '~/services/response';
import { useForceLoginOnAllScreens } from '~/hooks/use-force-login-on-all-screens';
import loginFlowManifest from '~/services/plugins/quick-brick-login-flow/config/manifest.ts';
import { useUserWayWidget } from '~/hooks/use-userway-widget';
import { useActions } from '~/hooks/actions/use-actions';
import { normalizeFeedServerAbortTimeoutMs } from '~/utils/get-json.server';
import { WebView } from '~/services/plugins/web-view-screen/components';

export const headers: HeadersFunction = ({ loaderHeaders }) => {
  const returnedHeaders: any = {};
  const cacheControl: string | null = loaderHeaders.get('Cache-Control');
  if (cacheControl) returnedHeaders['Cache-Control'] = cacheControl;
  const setCookie: string | null = loaderHeaders.get('Set-Cookie');
  if (setCookie) returnedHeaders['Set-Cookie'] = setCookie;

  return returnedHeaders;
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const metaTags: any[] = [
    ...cssVarsMetaFunctionData(data),
    {
      tagName: 'link',
      rel: 'canonical',
      href: data?.canonicalUrl,
    },
    ...(data?.metadata || []),
  ];

  if (data?.isSearchScreen) {
    metaTags.push({
      name: 'robots',
      content: 'noindex',
    });

    const additionalSearchScreenId = getAdditionalSearchScreenId(data);

    if (additionalSearchScreenId)
      metaTags.push(cssVarsMetaFunctionData(data, additionalSearchScreenId));
  }

  const tabsEntry = data?.tabs?.entry;
  const screens = data?.screens || [];
  const contentTypes = data?.contentTypes || {};

  if (
    data?.isTabsScreen &&
    tabsEntry &&
    !isEmptyArray(tabsEntry) &&
    screens.length > 0
  ) {
    tabsEntry.forEach((entry: any) => {
      const tabScreen =
        screens.find((s) => s.id === entry?.id) ||
        screens.find(
          (s) => s.id === contentTypes[entry?.type?.value || '']?.screen_id
        );

      if (tabScreen) {
        metaTags.unshift(
          ...cssVarsMetaFunctionData({ ...data, screen: tabScreen })
        );
      }
    });
  }

  return metaTags;
};

export async function loader(loaderFunctionArgs: LoaderFunctionArgs) {
  const { request } = loaderFunctionArgs;
  const url: URL = new URL(request.url);

  const supportedLanguages: string[] = getSupportedLanguages();
  const urlLanguage: string | 'unknown' = getURLLanguage(url.pathname);

  if (
    urlLanguage === supportedLanguages[0] &&
    url.pathname.startsWith(`/${urlLanguage}`)
  ) {
    const pathname = url.pathname.replace(`/${urlLanguage}`, '');
    return redirect(pathname, { status: 301 });
  }

  maybeLoginWithToken(url);

  const { debug } = getDebugMode(url);
  const layoutIdOverride = getLayoutIdOverride(url);

  const program = Effect.gen(function* (_) {
    const url = yield* _(RequestURL);
    const {
      screens,
      contentTypes,
      endpoints,
      pluginConfigurations,
      cellStyles,
      navigations,
      analyticsSharedVars,
      presetsMapping,
    } = yield* _(ZappConfig);

    const themeVars = getThemeVars(pluginConfigurations);

    const languagesLabels = getLanguagesLabels({
      supportedLanguages,
      pluginConfigurations,
    });

    const pathname: string = url.pathname;

    const q: string | null = url.searchParams.get('q');
    const filterTag: string | null = url.searchParams.get('filterTag');

    const isSearchScreen = getIsSearchScreen({
      pathname,
      supportedLanguages,
    });

    const { customConfiguration: caching } = getPluginConfig<
      typeof cachingManifest
    >(cachingManifest, pluginConfigurations);

    const {
      link_prefetch_switch: linkPrefetchSwitch,
      enable_feed_server_abort_timeout: enableFeedServerAbortTimeout,
      feed_server_abort_timeout_ms,
    } = caching;

    const feedServerAbortTimeoutMs: number = normalizeFeedServerAbortTimeoutMs(
      !isSearchScreen &&
        enableFeedServerAbortTimeout &&
        feed_server_abort_timeout_ms
    );

    const { screen, status, screenEntry } = yield* _(
      getCurrentScreen2({
        supportedLanguages,
        language: urlLanguage,
        warningThresholdMs: feedServerAbortTimeoutMs,
      })
    );

    const hasSmartComponents = screenHasSmartComponents(screen);

    const hash = getHashForCSS({
      screens,
      navigations,
      cellStyles,
      themeVars,
      smartComponentsHashInput: hasSmartComponents
        ? {
            screenId: screen.id,
            presetsMapping,
          }
        : undefined,
    });

    const { customConfiguration: webPoc } = getPluginConfig<
      typeof webPocManifest
    >(webPocManifest, pluginConfigurations);

    const trailerSource = webPoc?.trailerSource;

    yield* _(
      maybeRedirectToPlayer({
        screenEntry,
        trailerSource,
        language: urlLanguage,
      })
    );

    let searchData: GetSearchScreenData = yield* _(
      Effect.promise(() =>
        getSearchScreenData({ pathname, screens, searchQuery: q, filterTag })
      )
    );

    if (
      isSearchScreen &&
      searchData.enableSearchFilters &&
      searchData.filtersFeedSource
    ) {
      const filterEntries = yield* _(
        Effect.promise(() =>
          getSearchFilterEntries({
            filtersFeedSource: searchData.filtersFeedSource!,
            request,
            endpoints,
            pluginConfigurations,
          })
        )
      ).pipe(Effect.catchAll(() => Effect.succeed([])));
      searchData = { ...searchData, filterEntries };
    }

    const isTabsScreen = getIsTabsScreen(screen);

    const tabs = yield* _(
      Effect.promise(() => {
        if (!isTabsScreen) return Promise.resolve(null);
        return getScreenFeed({
          screen,
          uiComponents: [],
          dynamicIdFallback: screenEntry?.id,
          endpoints,
          pluginConfigurations,
          request,
          warningThresholdMs: feedServerAbortTimeoutMs,
        });
      })
    );

    const screenNavigations: NavigationHandler = getScreenNavigations({
      navigations,
      screens,
      currentScreen: screen,
      urlLanguage,
    });

    const footerData: { footerNavItems: FooterData[]; id: string } | undefined =
      getNavItems({
        navigations,
        navigationType: 'footer',
        screens,
        urlPathname: isSearchScreen ? `/${screen.id}` : url.pathname,
      });

    const uiComponents: UIComponent[] = addEndpointsTagsToUiComponents({
      uiComponents: screen.ui_components,
      endpoints,
    });

    const smartComponents = yield* _(
      Effect.promise(() =>
        transformSmartComponents({
          uiComponents,
          presetsMapping,
          pluginConfigurations,
          endpoints,
          request,
        })
      )
    );

    let originEntryUrl: string | undefined;

    if (url.searchParams.has('screen-feed')) {
      originEntryUrl = url.searchParams.get('screen-feed') || undefined;
    }

    const disableDynamicIdFallback =
      screen?.general?.disable_dynamic_id_fallback;

    const serverLoadedFeeds: ServerLoadedFeeds = (yield* _(
      Effect.promise(() =>
        getServerLoadedFeeds({
          uiComponents: smartComponents,
          dynamicIdFallback: screenEntry?.id,
          q,
          endpoints,
          pluginConfigurations,
          request,
          screenFeedEntry: screenEntry || {},
          disableDynamicIdFallback,
          feedServerAbortTimeoutMs,
        })
      )
    )).map((serverLoadedFeed) => {
      if (serverLoadedFeed?.feed) {
        return {
          ...serverLoadedFeed,
          feed: addWebLinks({
            feed: serverLoadedFeed?.feed,
            contentTypes,
            screens,
            trailerSource,
            originEntryUrl,
            language: urlLanguage,
            supportedLanguages,
          }),
        };
      }

      return serverLoadedFeed;
    });

    const tabsContentFeeds = yield* _(
      Effect.promise(() =>
        getTabsContentFeeds({
          tabs,
          screens,
          contentTypes,
          q,
          endpoints,
          pluginConfigurations,
          request,
          trailerSource,
          language: urlLanguage,
          supportedLanguages,
        })
      )
    );

    const searchScreens: Screen[] | undefined = getSearchScreens(screens);

    const searchScreenId: string | undefined = getScreenIdByScreenType(
      searchScreens,
      ['search_v2', 'qb_search_screen']
    );

    const baseURL = getBaseURL();

    const linksToScreenData = getLinksToScreenData(screens);

    const metadata = getMetadata(url, screen.general, {
      entry: [screenEntry],
    } as Feed);

    const { customConfiguration: userway } = getPluginConfig<
      typeof userwayManifest
    >(userwayManifest, pluginConfigurations);

    const { userway_account_id: userwayAccountId } = userway;

    const { customConfiguration: oneTrust } = getPluginConfig<
      typeof oneTrustManifest
    >(oneTrustManifest, pluginConfigurations);

    const { one_trust_id: oneTrustId } = oneTrust;

    const { customConfiguration: clarity } = getPluginConfig<
      typeof clarityManifest
    >(clarityManifest, pluginConfigurations);

    const clarityId = clarity?.project_id;

    const ketchScreen = getKetchScreen(screens);

    const ketchId = ketchScreen?.general?.tag_id;

    const analyticsCustomProperties =
      screenEntry.extensions?.analyticsCustomProperties &&
      JSON.stringify(screenEntry.extensions?.analyticsCustomProperties);

    const analyticsTrackData = {
      ...analyticsSharedVars,
      screen_name: screen.name,
      screen_type: screen.type,
      screen_title: screen.title,
      home_screen: screen.home,
      screen_entry_title: screenEntry.title,
      screen_entry_id: screenEntry.id,
      screen_entry_type: screenEntry.value?.type,
      screen_layout_id: screen.id,
      analyticsCustomProperties,
    };

    const dynamicIdFallback = screenEntry.id;

    const favoritesAction = getPluginConfig<typeof favoritesManifest>(
      favoritesManifest,
      pluginConfigurations
    );

    const { synced_feed: favoritesSyncedFeed } =
      favoritesAction?.customConfiguration;

    const isMock: boolean | undefined = process.env?.MOCK ? true : false;
    const DISABLE_THIRD_PARTY_SCRIPTS: string | undefined =
      process.env?.DISABLE_THIRD_PARTY_SCRIPTS;

    const isFavoritesSyncedFeed: boolean =
      isMock || isValidUrl(favoritesSyncedFeed?.source);

    const { customConfiguration: cw } = getPluginConfig<
      typeof continueWatchingManifest
    >(continueWatchingManifest, pluginConfigurations);

    const {
      synced_feed: continueWatchingSyncedFeed,
      auto_resume_maximum_items: maximumCWentries,
    } = cw;

    const isContinueWatchingSyncedFeed: boolean =
      isMock || isValidUrl(continueWatchingSyncedFeed?.source);

    const canonicalUrl: string = `${baseURL}${url.pathname}`;

    const newNavigations = Object.entries(navigations).reduce(
      (acc, [id, nav]) => {
        acc[id] = nav as ZappNavigation;
        return acc;
      },
      {} as Record<string, ZappNavigation>
    );

    const searchScreen: Screen | undefined = searchScreens?.[0];

    const navBar = getCurrentNavBar(
      isSearchScreen && searchScreen ? searchScreen : screen,
      newNavigations
    );

    const webviewUrl = yield* _(
      getWebviewUrl({
        screen,
        request,
        endpoints,
        pluginConfigurations,
        screenEntry,
      })
    );

    const isThemeV2: boolean = isThemeV2Enabled(pluginConfigurations);

    const { customConfiguration: defaultThemeV2Config } = getPluginConfig<
      typeof defaultThemeV2
    >(defaultThemeV2, pluginConfigurations);

    const timeZoneHeader: string | undefined = getTimeZoneHeader(request);

    const navigationAction = getPluginConfig<typeof navigationActionManifest>(
      navigationActionManifest,
      pluginConfigurations,
      false,
      urlLanguage
    );

    const cacheControl =
      ['web-products'].includes(screen?.type) ||
      isTrue(screen?.general?.disable_cache_control) ||
      hasClearCacheOnReloadRule(uiComponents)
        ? CACHE_CONTROL_NO_STORE
        : getPageCacheControlHeader(pluginConfigurations);

    const trailerAction = getPluginConfig<typeof trailerActionManifest>(
      trailerActionManifest,
      pluginConfigurations,
      true
    );

    const { customConfiguration: loginFlow } = getPluginConfig<
      typeof loginFlowManifest
    >(loginFlowManifest, pluginConfigurations);

    const isForceLoginOnAllScreens: boolean =
      loginFlow?.force_login_on_all_screens_switch;

    const deviceType = getDeviceType(request);

    return json(
      {
        metadata,
        trailerSource,
        baseURL,
        canonicalUrl,
        cellStyles,
        contentTypes,
        linksToScreenData,
        debug,
        navBar,
        navigations: screenNavigations,
        footerData,
        screen,
        searchScreens,
        searchScreenId,
        isSearchScreen,
        searchData,
        serverLoadedFeeds,
        isMock,
        hash,
        layoutIdOverride,
        uiComponents: smartComponents,
        oneTrustId,
        clarityId,
        ketchId,
        userwayAccountId,
        analyticsTrackData,
        isFavoritesSyncedFeed,
        favoritesSyncedFeed: favoritesSyncedFeed?.source,
        isContinueWatchingSyncedFeed,
        continueWatchingSyncedFeed: continueWatchingSyncedFeed?.source,
        webviewUrl,
        isThemeV2,
        defaultThemeV2Config,
        staticLinks: getStaticLinks(screens, supportedLanguages, urlLanguage),
        timeZoneHeader,
        navigationAction,
        trailerAction,
        favoritesAction,
        maximumCWentries,
        deviceType,
        supportedLanguages,
        language: urlLanguage,
        languagesLabels,
        linkPrefetchSwitch,
        DISABLE_THIRD_PARTY_SCRIPTS,
        dynamicIdFallback,
        isForceLoginOnAllScreens,
        screens,
        tabs,
        tabsContentFeeds,
        isTabsScreen,
        screenEntry,
      },
      {
        status,
        headers: {
          'Cache-Control': cacheControl,
        },
      }
    );
  });

  const runnable = program.pipe(
    Effect.provideService(ZappConfig, await getZappConfig(layoutIdOverride)),
    Effect.provideService(RequestURL, new URL(request.url)),
    Effect.provideService(ScreenReuqest, request)
  );

  const failureOrSuccess = await Effect.runPromiseExit(runnable);

  return Exit.match(failureOrSuccess, {
    onSuccess: (value) => {
      return value;
    },
    onFailure: (cause) => {
      const failure = Option.getOrUndefined(Cause.failureOption(cause));

      if (failure?._tag === 'No404ScreenSetError') {
        throw new Response(null, {
          status: 404,
          statusText: 'Not Found',
        });
      }

      if (failure?._tag === 'RedirectError') {
        throw redirect(failure?.redirectPath, { status: 302 });
      }
      throw cause;
    },
  });
}

async function getZappConfig(layoutIdOverride: string | null | undefined) {
  const { ttl, swr } = getConfigsCaching();

  const {
    layout,
    pluginConfigurations,
    endpoints,
    cellStyles,
    presetsMapping,
  } = await getConfig({ ttl, swr }, layoutIdOverride);

  const analyticsSharedVars = await getAnalyticsSharedVars(layout.id);

  const {
    screens,
    content_types: contentTypes,
    navigations,
    page_not_found,
  } = layout;
  return {
    screens,
    contentTypes,
    cellStyles,
    endpoints,
    pluginConfigurations,
    navigations,
    pageNotFoundScreenId: page_not_found,
    analyticsSharedVars,
    presetsMapping,
  };
}

export default function Index(): JSX.Element {
  const data = useLoaderData<typeof loader>();

  const {
    debug,
    screen,
    oneTrustId,
    clarityId,
    ketchId,
    analyticsTrackData,
    isMock,
    supportedLanguages,
    language,
    DISABLE_THIRD_PARTY_SCRIPTS,
    isFavoritesSyncedFeed,
    isContinueWatchingSyncedFeed,
  } = data;

  const screenHooksLength = screen?.hooks?.preload_plugins?.length || 0;
  const hasScreenHooks = screenHooksLength > 0;

  useScreensHistory(hasScreenHooks);

  const location = useLocation();

  useLanguageRedirect(supportedLanguages, language);

  useLoadIsLoggedIn();
  useMockIsLoggedIn(isMock);

  const isHookLoading: boolean = useScreenHooks(screen);

  useLoadFeed('favorites', isFavoritesSyncedFeed);
  useLoadFeed('continue-watching', isContinueWatchingSyncedFeed);

  useScreenAnalytics(analyticsTrackData);

  const { registry } = useActions();

  registry.updateContext({ data, location });

  const isForceLoginOnAllScreensLoading: boolean = useForceLoginOnAllScreens();

  useUserWayWidget();

  const screenType: ScreenType = screen?.type;

  if (isHookLoading || !screenType || isForceLoginOnAllScreensLoading)
    return <HookLoading />;

  return (
    <div key={location.pathname + location?.search} className="screen-opacity">
      {!DISABLE_THIRD_PARTY_SCRIPTS && !!oneTrustId && (
        <>
          <script
            src="https://cdn.cookielaw.org/scripttemplates/otSDKStub.js"
            type="text/javascript"
            data-domain-script={oneTrustId}
            defer
          ></script>

          <script
            defer
            dangerouslySetInnerHTML={{
              __html: `function OptanonWrapper() {}`,
            }}
          />
        </>
      )}

      {/* Microsoft Clarity */}
      {!DISABLE_THIRD_PARTY_SCRIPTS && !!clarityId && (
        <script
          defer
          dangerouslySetInnerHTML={{
            __html: `(function(c,l,a,r,i,t,y){
        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
      })(window, document, "clarity", "script", "${clarityId}");`,
          }}
        />
      )}

      {/* Ketch */}
      {!DISABLE_THIRD_PARTY_SCRIPTS && ketchId && (
        <script
          defer
          dangerouslySetInnerHTML={{
            __html: getKetchScript(ketchId),
          }}
        />
      )}

      <ClientOnly fallback={<></>}>{() => <NetworkStatusBanner />}</ClientOnly>
      {debug && <div className="fixed z-10 h-1 w-full bg-orange-500"></div>}

      <NavBar />
      <NewNavBar />

      {[
        'general_content',
        'search_v2',
        'qb_search_screen',
        'web-products',
        'quick-brick-tabs',
      ].includes(screenType) && <GeneralContent />}

      {screenType === 'webview_screen_qb' && <WebView />}

      <Footer />
    </div>
  );
}
